import React from 'react';

/**
 * Verbal Order Authentication Page - Reduce administrative burden and enhance compliance
 * Based on Videxa's Verbal Order Authentication solution
 */
const VerbalOrderAuthPage = () => {
  return (
    <div className="verbal-order-auth-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-green-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Verbal Order <span className="text-green-300">Authentication</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-green-100">
              Reduce the administrative burden for physicians and nurses, enhance compliance, accelerate cash, and improve outcomes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors">
                Get Started
              </button>
              <button className="border-2 border-green-300 text-green-300 hover:bg-green-300 hover:text-green-900 px-8 py-4 rounded-lg font-semibold transition-colors">
                Browse Features
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What is VOA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900 dark:text-white text-center">
              What is the AgentNexus VOA Solution?
            </h2>
            <div className="text-lg text-gray-700 dark:text-gray-300 space-y-6">
              <p>
                The AgentNexus Verbal Order Authentication (VOA) solution is a complete solution for clinical staff, the back office and physicians that closes the communication gap across the healthcare landscape:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                <div className="text-center">
                  <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg mb-4">
                    <h3 className="font-bold text-blue-600 dark:text-blue-300">Fully Automated</h3>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-green-100 dark:bg-green-900 p-4 rounded-lg mb-4">
                    <h3 className="font-bold text-green-600 dark:text-green-300">EMR Integrated</h3>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-purple-100 dark:bg-purple-900 p-4 rounded-lg mb-4">
                    <h3 className="font-bold text-purple-600 dark:text-purple-300">Real-time Data</h3>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-orange-100 dark:bg-orange-900 p-4 rounded-lg mb-4">
                    <h3 className="font-bold text-orange-600 dark:text-orange-300">Mobile Application</h3>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mt-8">
                <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Key Features Include:</h3>
                <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                  <li>• Dashboards with real-time visibility</li>
                  <li>• KPI tracking and monitoring</li>
                  <li>• Curated insights per executive persona</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Benefits Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Executive Benefits
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12">
            AgentNexus Verbal Order Authentication can help your organization realize your vision.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* CFO Benefits */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-4 text-blue-600">Chief Financial Officer</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Create new opportunities with analytics, machine learning, and actionable intelligence across your health data.
              </p>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• Consistency and accuracy of orders is ensured</li>
                <li>• Accelerate cash, reduce costs, and decrease claim denials</li>
                <li>• "One source of truth" throughout the entire process</li>
                <li>• Visibility into unsigned orders that are costing hospital money, and ability to 'drilldown'</li>
              </ul>
            </div>

            {/* CMO Benefits */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-4 text-green-600">Chief Medical Officer</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Transform health outcomes using data-driven insights to improve clinical decision-making, help reduce process latency, and create proactive care plans to help reduce costs.
              </p>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• Real-time KPIs and visibility to ensure physicians are compliant</li>
                <li>• Easily review the record and provide feedback for an incorrectly entered order</li>
                <li>• Day to day management of expectations with executives and the medical community</li>
              </ul>
            </div>

            {/* CCO Benefits */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-4 text-purple-600">Chief Compliance Officer</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Improve operational efficiency by ensuring compliance efforts across clinicians and administrators revealing actionable insights.
              </p>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• Physicians authenticate via mobile device while off duty or away from the hospital</li>
                <li>• Easily review the record and provide feedback for an incorrectly entered order</li>
                <li>• Effortlessly collaborate with the care team to resolve issues</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Benefits Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Clinical Benefits
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Physicians */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-blue-600 dark:text-blue-300">Physicians</h3>
              <ul className="space-y-4 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-3">•</span>
                  Ability to authenticate 'anytime, anywhere' via mobile device while off duty or away from the hospital
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-3">•</span>
                  Easily review the record and provide feedback for an incorrectly entered order
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-3">•</span>
                  Effortlessly collaborate with the care team
                </li>
              </ul>
            </div>

            {/* Nurses */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900 dark:to-green-800 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-green-600 dark:text-green-300">Nurses</h3>
              <ul className="space-y-4 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-green-500 mr-3">•</span>
                  Collaborate with physicians and the care team
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3">•</span>
                  Reduced administration burden to allow for more clinical time
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Operational Benefits Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Operational Benefits
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Revenue Cycle */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-6 text-purple-600">Revenue Cycle</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• Real-time visibility across the organization and the impact of all unauthenticated or outstanding verbal orders on A/R</li>
                <li>• Accelerate claim approvals</li>
                <li>• Minimize claim denials</li>
                <li>• Reduce admin burden professional staff time spent on administration</li>
                <li>• Strictly enforce timing requirements for authentication</li>
              </ul>
            </div>

            {/* Medical Records */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-6 text-orange-600">Medical Records</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li>• Consistency and accuracy of orders ensured</li>
                <li>• "One source of truth" throughout the entire process</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Challenges of Current VOA Processes
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12">
            Minimizing misinterpretation, reimbursement implications and admin burden of Verbal Order Authentications
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-red-50 dark:bg-red-900 p-6 rounded-lg text-center">
              <h3 className="text-lg font-bold mb-3 text-red-600 dark:text-red-300">Delayed Revenue</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Unauthenticated verbal orders significantly delay revenue collection
              </p>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900 p-6 rounded-lg text-center">
              <h3 className="text-lg font-bold mb-3 text-orange-600 dark:text-orange-300">Increased Costs</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Administrative burden increases operational costs significantly
              </p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900 p-6 rounded-lg text-center">
              <h3 className="text-lg font-bold mb-3 text-yellow-600 dark:text-yellow-300">Claim Denials</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Increased risk of claim denials due to authentication issues
              </p>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900 p-6 rounded-lg text-center">
              <h3 className="text-lg font-bold mb-3 text-purple-600 dark:text-purple-300">Non-compliance Risk</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Regulatory compliance challenges and audit risks
              </p>
            </div>
          </div>

          <div className="mt-12 bg-yellow-50 dark:bg-yellow-900 p-8 rounded-lg">
            <h3 className="text-2xl font-bold mb-4 text-yellow-700 dark:text-yellow-300">Current Process Challenges</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold mb-2 text-gray-900 dark:text-white">Administrative Burden</h4>
                <p className="text-gray-700 dark:text-gray-300">
                  Verbal order process contributes significantly to administrative burden and burnout of staff, taking time away from their primary role and function.
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-gray-900 dark:text-white">Risk of Error</h4>
                <p className="text-gray-700 dark:text-gray-300">
                  High risk of error, misinterpretation or incorrect fulfillment. Verbal orders leave room for error and potential adverse patient incidents.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            AgentNexus Can Help
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            AgentNexus brings disparate healthcare data sets together into one consolidated environment for streamlined verbal order authentication.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-green-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors">
              Request Demo
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-green-900 px-8 py-4 rounded-lg font-semibold transition-colors">
              View Our Plans
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VerbalOrderAuthPage; 